import { Check, PiggyBank, Warning } from '@nackle/icons';
import { Tag } from '@nackle/paper';
import { RouteComponentProps } from '@reach/router';
import { listTokens, TokenView } from 'app/api/tokens';
import { groupBy, mapValues } from 'lodash';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getRoomMap, loadArcades } from 'routes/tokens/create/dux';
import { RoomLabel } from 'routes/tokens/create/RoomCabinetsTooltip';
import { PersonSearchField } from 'shared/person-search-field';
import { useActionCreator, useInit } from 'utils/effects';
import { useQueryParam } from 'utils/useQueryParam';

const REDEEMED_ICON = <Check/>;
const AVAILABLE_ICON = <PiggyBank/>;
const FAILED_ICON = <Warning/>;
const SMALL_DASH = '-';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function TokenList (props: RouteComponentProps) {
    useInit(useActionCreator(loadArcades));
    const rooms = useSelector(getRoomMap);

    const [personIdOrArray, setPersonId] = useQueryParam('id', props);
    const personId: string | undefined = (Array.isArray(personIdOrArray) ? personIdOrArray[0] : personIdOrArray) || undefined;
    const [tokens, setTokens] = useState<TokenView[]>([]);

    useEffect(
        () => {
            if (!personId) {
                setTokens([]);
                return ;
            }

            listTokens(personId).then(
                ({ tokens }) => {
                    setTokens(tokens);
                },
                (error) => console.error(error),
            );
        },
        [personId]
    );

    const tokensByRoom: { [roomId: string]: TokenView[] } = useMemo(() => groupBy(tokens, 'roomId'), [tokens]);

    const tokensByRoomAndState: { [roomId: string]: { [tokenState: string]: TokenView[] } } = useMemo(() => {
        return mapValues(tokensByRoom, (tokens: TokenView[]) => groupBy(tokens, 'state'));
    }, [tokensByRoom]);

    const handleChangeRecipients = useCallback(
        async (personIds: string[]) => {
            // Only one person can be set at a time
            const personId = personIds.slice(-1);
            setPersonId(personId);
        },
        [setPersonId]
    );
   
    const total = tokens.length;
    const totalAvailable = tokens.filter(token => token.state === 'available').length;
    const totalRedeemed = tokens.filter(token => token.state === 'used' || token.state === 'used_autoplay').length;
    const totalPlayed = tokens.filter(token => token.state === 'used').length;
    const totalAutoplayed = tokens.filter(token => token.state === 'used_autoplay').length;
    const totalFailed = tokens.filter(token => token.state === 'failed').length;
    return (
        <div className="page-content">
            <section className='main-section'>
                <h3 className="section-header">List Tokens</h3>
                    <div className="search-section">
                        <PersonSearchField
                            id="list-token-form-person-id"
                            label="Look up tokens by person"
                            onChange={handleChangeRecipients}
                            personIds={personId ? [personId] : []}
                        />
                </div>
                {personId &&
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ paddingTop: 20, width: '100%', verticalAlign: 'top', textAlign: 'inherit' }}>Room</th>
                                <th style={{ textAlign: 'center', padding: 11 }}>Total</th>
                                <th style={{ textAlign: 'center', padding: 11 }}>Available</th>
                                <th style={{ textAlign: 'center', padding: 11 }}>Redeemed</th>
                                <th style={{ textAlign: 'center', padding: 11 }}>Manually Played</th>
                                <th style={{ textAlign: 'center', padding: 11 }}>Auto-Played</th>
                                <th style={{ textAlign: 'center', padding: 11 }}>Failed</th>
                            </tr>
                            <tr>
                                <td>All Rooms</td>
                                <td style={{ textAlign: 'center' }}>{total}</td>
                                <td style={{ textAlign: 'center' }}>{totalAvailable ? <>{AVAILABLE_ICON} {totalAvailable}</> : SMALL_DASH}</td>
                                <td style={{ textAlign: 'center' }}>{totalRedeemed ? <>{REDEEMED_ICON} {totalRedeemed}</> : SMALL_DASH}</td>
                                <td style={{ textAlign: 'center' }}>{totalPlayed ? totalPlayed : SMALL_DASH}</td>
                                <td style={{ textAlign: 'center' }}>{totalAutoplayed ? totalAutoplayed : SMALL_DASH}</td>
                                <td style={{ textAlign: 'center' }}>{totalFailed ? <>{FAILED_ICON} {totalFailed}</> : SMALL_DASH}</td>
                            </tr>
                        </thead>
                        <tbody>
                        {
                            Object.entries(tokensByRoom).map(([roomId, roomTokens]) => {
                                const room = rooms[roomId];

                                const {
                                    available: { length: available } = [],
                                    used: { length: played } = [],
                                    used_autoplay: { length: autoplayed } = [],
                                    failed: { length: failed } = [],
                                } = tokensByRoomAndState[roomId];

                                const redeemed = played + autoplayed;

                                return (
                                    <tr key={roomId}>
                                        <td>
                                            {room ?
                                                <RoomLabel item={room} /> :
                                                <>Unknown Room <Tag>{roomId}</Tag></>
                                            }
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{roomTokens.length ? roomTokens.length : SMALL_DASH}</td>
                                        <td style={{ textAlign: 'center' }}>{available ? <>{AVAILABLE_ICON} {available}</> : SMALL_DASH}</td>
                                        <td style={{ textAlign: 'center' }}>{redeemed ? <>{REDEEMED_ICON} {redeemed}</> : SMALL_DASH}</td>
                                        <td style={{ textAlign: 'center' }}>{played ? played : SMALL_DASH}</td>
                                        <td style={{ textAlign: 'center' }}>{autoplayed ? autoplayed : SMALL_DASH}</td>
                                        <td style={{ textAlign: 'center' }}>{failed ? <>{FAILED_ICON} {failed}</> : SMALL_DASH}</td>
                                    </tr>
                                );
                            })
                        }
                        </tbody>
                    </table>
                }
            </section>
        </div>
    );
}

